import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { isKenoType } from '../../../utils/LotteryHelpers';
import { LineNumbers } from '../Types/Types';
import * as lotteryStoreAction from '../redux/lotteryStoreSlice';
import { RootState } from '../redux/store';
import * as action from '../redux/storeSlice';

import { generateRandomLine, isBonusNumberPickable, isNonEmptyLine } from './linesHelper';

export default function useLinesHelper() {
    const dispatch = useDispatch();
    const { lines: storeLines, lottery, permsPickerLines, stakes: storeStakes, stakeIsValid } = useSelector(
        (state: RootState) => state.playStore
    );
    const { pickerIndex, banker, permutations, isPermsPickerEnabled } = useSelector(
        (state: RootState) => state.lotteryPlayStore
    );
    const { numbersToPlay } = useSelector((state: RootState) => state.kenoPlayStore);
    const ranges = lottery.lotteryRule.ranges;
    const numbersPerLine = isBonusNumberPickable(lottery)
        ? lottery.lotteryRule.numbers_per_line
        : [lottery.lotteryRule.numbers_per_line[0]];

    const EMPTY_LINE = useRef<LineNumbers>(numbersPerLine.map(() => [])).current;

    const setLines = (lines: LineNumbers[]) => dispatch(action.setLines(lines));
    const setStakes = (stakes: number[]) => dispatch(action.setStakes(stakes));

    const setStakeIsValid = (stakeIsValid: boolean) => dispatch(action.setStakeIsValid(stakeIsValid));

    const setLineStake = (index: number, stake: number) =>
        dispatch(action.setLineStake({ lineIndex: index, lineStake: stake }));
    const setLine = (index: number, line: LineNumbers) =>
        dispatch(action.setLine({ lineIndex: index, lineNumbers: line }));

    const setIsPermsPickerEnabled = (isPermsPickerEnabled: boolean) => {
        dispatch(lotteryStoreAction.setIsPermsPickerEnabled(isPermsPickerEnabled));
    };

    const setPermutations = (permutations: number[] | null) => {
        dispatch(lotteryStoreAction.setPermutations(permutations));
    };
    const setBanker = (index: number | null) => {
        dispatch(lotteryStoreAction.setBanker(index));
    };
    const setEmptyLine = (index: number) => {
        dispatch(action.setLine({ lineIndex: index, lineNumbers: EMPTY_LINE }));
        setPermutations(null);
        if (isPermsPickerEnabled) {
            setLines([[[]]]);
        }
        setIsPermsPickerEnabled(false);
        setBanker(null);
    };

    const addEmptyLine = () => dispatch(action.addLine(EMPTY_LINE));
    const addRandomLine = () => {
        const maxNumbersPerLine = isKenoType(lottery.type) ? [numbersToPlay] : numbersPerLine;
        const randomLine = generateRandomLine(ranges, maxNumbersPerLine);
        dispatch(action.addLine(randomLine));
    };

    const setRandomLine = (index: number) => {
        const maxNumbersPerLine = isKenoType(lottery.type) ? [numbersToPlay] : numbersPerLine;
        dispatch(action.setLine({ lineIndex: index, lineNumbers: generateRandomLine(ranges, maxNumbersPerLine) }));
    };

    const removeLine = (index: number) => dispatch(action.removeLine(index));
    const removeStake = (index: number) => dispatch(action.removeStake(index));

    const multipleQuickPick = (count: number) => {
        let picksLeft = count;
        for (let index = 0; index < storeLines.length && picksLeft > 0; index++) {
            if (!isNonEmptyLine(storeLines[index])) {
                setRandomLine(index);
                picksLeft--;
            }
        }
        picksLeft = Math.min(picksLeft, lottery.maxLinesPerTicket - storeLines.length);
        for (; picksLeft > 0; picksLeft--) {
            addRandomLine();
        }
    };

    const triggerChooseLotteryMode = (isTriggered: boolean) => {
        dispatch(lotteryStoreAction.triggerChooseLotteryMode(isTriggered));
    };

    const setPermsModeChosen = (isChosen: boolean) => {
        dispatch(lotteryStoreAction.setPermsModeChosen(isChosen));
    };

    const setCurrentPickerIndex = (index: number) => {
        dispatch(lotteryStoreAction.setCurrentPickerIndex(index));
    };

    const setPermsPickerLines = (lines: number[][]) => dispatch(action.setPermsPickerLines(lines));

    const removeAllLines = (lines?: LineNumbers[]) => {
        switch (lottery.type) {
            case 'keno':
                setLines([[[]]]);
                break;
            case 'lottery':
                lines?.forEach((_, i) => setEmptyLine(i));
                break;
            case 'pick':
                setLines([[]]);
                break;
            default:
                break;
        }
    };

    return {
        lines: storeLines,
        stakes: storeStakes,
        setLines,
        setStakes,
        setStakeIsValid,
        stakeIsValid,
        setLineStake,
        setLine,
        setEmptyLine,
        addEmptyLine,
        addRandomLine,
        setRandomLine,
        removeLine,
        removeStake,
        lottery,
        ranges,
        numbersPerLine,
        EMPTY_LINE,
        numbersToPlay,
        isBonusNumberPickable,
        multipleQuickPick,
        triggerChooseLotteryMode,
        setPermsModeChosen,
        setCurrentPickerIndex,
        pickerIndex,
        setBanker,
        banker,
        permutations,
        setPermutations,
        permsPickerLines,
        setPermsPickerLines,
        setIsPermsPickerEnabled,
        isPermsPickerEnabled,
        removeAllLines,
    };
}
